import React, { useRef } from "react";
import { useDrag, useDrop, DropTargetMonitor, DragSourceMonitor } from "react-dnd";
import classNames from "classnames";

const ItemTypes = {
  CARD: "card",
};

export interface CardInfo {
  id: number;
  enabled?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldElement?: any;
}

interface CardProps {
  index: number;
  info: CardInfo;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  WrappedComponent?: typeof React.Component;

  removeCard: Function;
  removeCardEnabled?: boolean;
  toggleAlignment?: boolean;
  disableAllFields?: boolean;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const Card: React.FC<CardProps> = (props: CardProps): JSX.Element => {
  const { index, info, moveCard, WrappedComponent, removeCard, removeCardEnabled, toggleAlignment = false, disableAllFields } = props;
  let grippyRef = useRef(null);
  if (disableAllFields) {
    grippyRef = null;
  }
  const [, drop] = useDrop({
    accept: ItemTypes.CARD,
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!grippyRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = grippyRef.current && grippyRef.current.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      /** Only perform the move when the mouse has crossed half of the items height
      When dragging downwards, only move when the cursor is below 50%
      When dragging upwards, only move when the cursor is above 50% **/

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);

      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.CARD, id: info.id, index },
    type: ItemTypes.CARD,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    collect: (monitor: DragSourceMonitor): any => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(grippyRef));

  const { enabled } = info;

  let numClass = "caprx-sortable-list-num";
  let grippyClass = "caprx-icon-drag caprx-sortable-list-grippy";

  let numListElement = <div className={numClass}>{<span>{index + 1}</span>}</div>;

  if (enabled !== undefined) {
    numClass = classNames("caprx-sortable-list-num", {
      "num-disabled": !enabled || disableAllFields,
    });

    grippyClass = classNames("caprx-icon-drag caprx-sortable-list-grippy", {
      "grippy-disabled": !enabled || disableAllFields,
    });

    numListElement = <div className={numClass}>{enabled && <span>{index + 1}.</span>}</div>;
  }

  return (
    <div ref={grippyRef} className={"caprx-sortable-list-card"} style={{ opacity }}>
      <div className="caprx-sortable-list-card-header">
        {numListElement}
        {!toggleAlignment && <div ref={grippyRef} className={grippyClass} />}
      </div>
      <div className="caprx-sortable-list-card-content">{WrappedComponent && <WrappedComponent {...info} />}</div>
      {removeCardEnabled && (
        <div className="caprx-sortable-list-card-remove-btn caprx-icon-inputclose" onClick={(): void => removeCard(index)} />
      )}
      {toggleAlignment && <div ref={grippyRef} className={grippyClass} />}
    </div>
  );
};

export default Card;
